import { format, parse } from 'date-fns';
import { z } from 'zod';

import {
  type InferFormValues,
  defineForm,
} from '@/lib/form-contract/define-form';
import { zonedEvent, zonedWallTimeToUtc } from '@/lib/timezone';
import type {
  AppointmentColor,
  CreateAppointmentInput,
  CreateAppointmentVisitInput,
} from '@borradh-workspace/api-client/types';
import {
  FALLBACK_APPOINTMENT_DURATION_MINUTES,
  resolveAppointmentDuration,
} from '@borradh-workspace/labels';

/**
 * SHARED CORE — appointment creation.
 *
 * ONE `defineForm` declaration and ONE payload builder for every surface that
 * creates an appointment (the desktop dialog and the two mobile funnels).
 * Nothing else in the app may hand-build a `POST /appointments` body — see the
 * form contract (`create-appointment.contract.test.tsx`), which drives all three
 * surfaces through their real UI and pins them to the exact same output.
 */

/**
 * Fallback length (minutes) when neither the chosen service nor the
 * organization configures one.
 *
 * Re-exported from the shared vocabulary rather than declared here. This
 * constant used to be a local `60` while the public booking page used a local
 * `30`, so the same null-duration service produced a 60-minute staff block and
 * a 30-minute customer slot (ENG-793). There is now one ladder —
 * service → org default → 30 — in `resolveAppointmentDuration`.
 */
export const DEFAULT_APPOINTMENT_DURATION_MINUTES =
  FALLBACK_APPOINTMENT_DURATION_MINUTES;

/** Fallback tint when the assigned practitioner has no color set. */
export const DEFAULT_APPOINTMENT_COLOR: AppointmentColor = 'blue';

/**
 * Sentinel for "no specific team member" in the mobile practitioner step.
 * Stripped by the payload builder (the key is omitted entirely), matching a
 * desktop slot that has no practitioner column.
 */
export const APPOINTMENT_NO_PRACTITIONER = 'none';

/**
 * The form, declared once: schema + label + control + default per key. The JSX
 * renders `appointmentCreateForm.labels.x` rather than a literal, so a control
 * deleted from a surface has nowhere to hide — the contract's locator misses it.
 *
 * The controls named here are the DESKTOP dialog's (a combobox for the
 * service, a Select for the team member, an `<input type="time">`, a popover DatePicker). The mobile
 * funnels reach the same fields through their own controls — a full-screen
 * client list, a service list, practitioner pills, a time sheet — so their
 * surfaces in the contract carry their own fills. Both still locate a REAL
 * control and still fail loudly when one is missing.
 */
export const appointmentCreateForm = defineForm({
  fields: {
    leadId: {
      schema: z.string().min(1, 'Client is required'),
      label: 'Client',
      control: 'custom',
      default: '',
      sample: 'lead-1',
    },
    serviceId: {
      schema: z.string().min(1, 'Service is required'),
      label: 'Service',
      // Desktop: a multi-select combobox (the first ticked service lands
      // here; the rest are added services). Mobile picks it from a list step.
      control: 'combobox',
      default: '',
      sample: 'svc-1',
      sampleLabel: 'Balayage',
    },
    /** Optional; `APPOINTMENT_NO_PRACTITIONER` means "unassigned". */
    practitionerId: {
      schema: z.string().optional(),
      label: 'Team member',
      control: 'select',
      default: '',
      sample: 'prac-1',
      sampleLabel: 'Sam',
    },
    /** yyyy-MM-dd, wall-clock in the business timezone → folded into startDate. */
    date: {
      schema: z.string().min(1, 'Date is required'),
      label: 'Date',
      control: 'custom',
      default: '',
      sample: '2026-03-05',
      derived: true,
    },
    /** HH:mm, wall-clock in the business timezone → folded into startDate. */
    startTime: {
      schema: z.string().min(1, 'Start time is required'),
      label: 'Start time',
      control: 'time',
      default: '',
      sample: '09:30',
      derived: true,
    },
    /** Free text → the appointment's `description`. */
    notes: {
      schema: z.string().optional(),
      label: 'Notes',
      control: 'textarea',
      default: '',
      sample: 'Allergic to latex',
      derived: true,
    },
  },
});

export const appointmentCreateSchema = appointmentCreateForm.schema;
export const appointmentCreateFields = appointmentCreateForm.fields;
export const appointmentCreateDefaultValues = appointmentCreateForm.defaults;

export type AppointmentCreateFormData = InferFormValues<
  typeof appointmentCreateForm
>;

/** Minimal shape the core needs off a service. */
export interface AppointmentCreateService {
  id: string;
  name: string;
  appointmentDuration?: number | string | null;
  /** Held as a Google Meet video call. */
  virtualMeeting?: boolean | null;
}

/** Minimal shape the core needs off a practitioner. */
export interface AppointmentCreatePractitioner {
  id: string;
  name: string;
  color?: string | null;
  /** The services they are assigned to (`practitioner_service`). */
  services?: ReadonlyArray<{ serviceId: string }> | null;
}

export interface AppointmentCreateContext {
  services: AppointmentCreateService[];
  practitioners: AppointmentCreatePractitioner[];
  /** organization.timezone — the business timezone the picked wall-clock is in. */
  timeZone: string;
  /**
   * `organization.defaultAppointmentDuration` — the clinic-wide length used for
   * a service that configures none. The SAME rung the public booking page,
   * the chatbot and the voice agent resolve through, which is what stops the
   * staff calendar and the booking page disagreeing (ENG-793).
   */
  defaultAppointmentDuration?: number | null;
}

/**
 * The service's own length; never snapped to buckets.
 *
 * Falls through the shared ladder when the service has no duration, so a
 * booking made here is the same length as one made from the public page.
 */
export function resolveAppointmentDurationMinutes(
  service: AppointmentCreateService | undefined,
  organizationDefault?: number | null
): number {
  return resolveAppointmentDuration(
    service?.appointmentDuration,
    organizationDefault
  );
}

/**
 * The team members who may be booked for a service — the client-side mirror
 * of the server's rule (`canPerformService` in the features package):
 *
 *   - nobody assigned to the service → anyone on the list;
 *   - somebody assigned              → only those people.
 *
 * No service picked yet → everyone, so the picker is never empty before the
 * user has chosen what they are booking. The server re-checks a multi-service
 * visit, so a stale list cannot book someone who does not do the service.
 */
export function practitionersForService<
  P extends AppointmentCreatePractitioner,
>(practitioners: readonly P[], serviceId: string | undefined): P[] {
  if (!serviceId) return [...practitioners];
  const assigned = practitioners.filter((p) =>
    p.services?.some((link) => link.serviceId === serviceId)
  );
  return assigned.length > 0 ? assigned : [...practitioners];
}

/** May this team member be booked for this service? Unassigned is always fine. */
export function canPractitionerPerformService(
  practitioners: readonly AppointmentCreatePractitioner[],
  serviceId: string | undefined,
  practitionerId: string | undefined
): boolean {
  const id = resolveAppointmentPractitionerId(practitionerId);
  if (!id) return true;
  return practitionersForService(practitioners, serviceId).some(
    (p) => p.id === id
  );
}

/**
 * A service added to the booking after the first: what is done and who does
 * it. The first service stays on the form's own `serviceId` / `practitionerId`,
 * so a one-service booking is exactly the booking it always was.
 */
export interface AppointmentAdditionalService {
  /** Stable React key; never sent. */
  key: string;
  serviceId: string;
  /** A team member id, or `APPOINTMENT_NO_PRACTITIONER` / '' for unassigned. */
  practitionerId: string;
}

/**
 * The `POST /appointments/visit` body for a booking with more than one
 * service. Same start-time resolution as {@link buildCreateAppointmentPayload};
 * title, colour and every end time are derived per service by the server.
 */
export function buildCreateAppointmentVisitPayload(
  values: AppointmentCreateFormData,
  additional: readonly AppointmentAdditionalService[],
  context: Pick<AppointmentCreateContext, 'timeZone'>
): CreateAppointmentVisitInput {
  const [hours, minutes] = values.startTime.split(':').map(Number);
  const day = parse(values.date, 'yyyy-MM-dd', new Date());
  const startDate = zonedWallTimeToUtc(
    day,
    hours || 0,
    minutes || 0,
    context.timeZone
  );
  const notes = values.notes?.trim();
  const line = (serviceId: string, practitionerId: string | undefined) => {
    const id = resolveAppointmentPractitionerId(practitionerId);
    return id ? { serviceId, practitionerId: id } : { serviceId };
  };

  return {
    leadId: values.leadId,
    startDate: startDate.toISOString(),
    ...(notes ? { description: notes } : {}),
    services: [
      line(values.serviceId, values.practitionerId),
      ...additional.map((item) => line(item.serviceId, item.practitionerId)),
    ],
  } as CreateAppointmentVisitInput;
}

/** Normalise the optional practitioner selection to an id or `undefined`. */
export function resolveAppointmentPractitionerId(
  value: string | undefined
): string | undefined {
  if (!value || value === APPOINTMENT_NO_PRACTITIONER) return undefined;
  return value;
}

/**
 * THE ONLY place a create-appointment API payload is constructed.
 *
 * - title  — auto-derived from the chosen service (never typed by the user)
 * - color  — derived from the assigned practitioner's calendar tint
 * - end    — start + the service's exact `appointmentDuration`
 * - start  — the picked wall-clock resolved in the BUSINESS timezone
 */
export function buildCreateAppointmentPayload(
  values: AppointmentCreateFormData,
  context: AppointmentCreateContext
): CreateAppointmentInput {
  const service = context.services.find((s) => s.id === values.serviceId);
  const practitionerId = resolveAppointmentPractitionerId(
    values.practitionerId
  );
  const practitioner = practitionerId
    ? context.practitioners.find((p) => p.id === practitionerId)
    : undefined;

  const [hours, minutes] = values.startTime.split(':').map(Number);
  const day = parse(values.date, 'yyyy-MM-dd', new Date());
  const startDate = zonedWallTimeToUtc(
    day,
    hours || 0,
    minutes || 0,
    context.timeZone
  );

  const durationMinutes = resolveAppointmentDurationMinutes(
    service,
    context.defaultAppointmentDuration
  );
  const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);

  const color =
    (practitioner?.color as AppointmentColor | undefined) ??
    DEFAULT_APPOINTMENT_COLOR;

  const notes = values.notes?.trim();

  return {
    title: service?.name ?? 'Appointment',
    description: notes ? notes : undefined,
    startDate,
    endDate,
    color,
    leadId: values.leadId,
    serviceId: values.serviceId,
    ...(practitionerId ? { practitionerId } : {}),
  } as CreateAppointmentInput;
}

export interface AppointmentCreateDefaultsInput {
  /** Organization timezone used for the current-time default. */
  timeZone?: string;
  /** The calendar day the slot sits on. */
  slotDate?: Date;
  /** Wall-clock time of the clicked slot, in the business timezone. */
  startTime?: { hour: number; minute: number };
  leadId?: string;
  serviceId?: string;
  practitionerId?: string;
  notes?: string;
}

/** Seed form values from a calendar slot / funnel search params. */
export function appointmentCreateDefaults({
  timeZone = 'UTC',
  slotDate,
  startTime,
  leadId,
  serviceId,
  practitionerId,
  notes,
}: AppointmentCreateDefaultsInput): AppointmentCreateFormData {
  const now = zonedEvent(new Date().toISOString(), timeZone);
  // A selected calendar day carries local date fields, not an event instant.
  // Preserve it; derive only the unsupplied time from the clinic clock.
  const day = slotDate ?? now;
  const hour = startTime?.hour ?? now.getHours();
  const minute = startTime?.minute ?? now.getMinutes();

  return {
    ...appointmentCreateForm.defaults,
    leadId: leadId ?? '',
    serviceId: serviceId ?? '',
    practitionerId: practitionerId ?? '',
    date: format(day, 'yyyy-MM-dd'),
    startTime: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
    notes: notes ?? '',
  };
}
